import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { METHOD, MEDIA } from "@/data/content";

const PORTFOLIO_SECTORS = [
  "Concessionarie", "Negozi", "Locali", "Ristoranti",
  "Animazione", "Professionisti", "Centri benessere", "Aziende locali",
];

export function Portfolio() {
  const reels = [MEDIA.editing, MEDIA.camera, MEDIA.heroFallback, MEDIA.editing];
  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-24 lg:py-32 bg-anthracite/40 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Portfolio</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Attività reali. Contenuti reali.
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Realizziamo video verticali in formato Reel per concessionarie, negozi, locali,
            ristoranti, attività di animazione, professionisti, centri benessere e aziende locali.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2.5">
          {PORTFOLIO_SECTORS.map((s, i) => (
            <span key={i} data-testid={`portfolio-sector-${i}`} className="rounded-full border border-white/12 bg-[#0b0b0b] px-4 py-1.5 text-xs text-slate-300">
              {s}
            </span>
          ))}
        </div>

        {/* Vertical Reel placeholders (ready for real content) */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {reels.map((src, i) => (
            <motion.div
              key={i}
              data-testid={`portfolio-media-${i}`}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 aspect-[9/16]"
            >
              <img src={src} alt="Contenuto Reel" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-[#050505]/30 flex items-center justify-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-light/90 text-[#050505]">
                  <Play size={18} fill="currentColor" />
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 mx-auto max-w-3xl text-center text-sm text-slate-400 leading-relaxed border border-white/8 rounded-2xl bg-[#0b0b0b] px-6 py-5">
          Ogni progetto potrà mostrare nome dell'attività, settore, obiettivo, tipologia di
          contenuti e video realizzati. Quando disponibile, evidenziamo dati reali e verificabili —
          come un contenuto che ha ottenuto complessivamente circa <span className="text-white">270.000 visualizzazioni</span> tra
          Instagram e TikTok — sempre riferiti al contenuto reale. Spazio pronto per inserire i tuoi video.
        </p>
      </div>
    </section>
  );
}

export function Method() {
  return (
    <section id="metodo" data-testid="method-section" className="relative py-24 lg:py-32">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Il metodo</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Come iniziamo a lavorare insieme
          </h2>
        </div>

        <div className="mt-14 relative">
          <div className="absolute left-[27px] lg:left-1/2 top-0 bottom-0 w-px bg-white/10" />
          <div className="space-y-10">
            {METHOD.map((m, i) => (
              <motion.div
                key={m.n}
                data-testid={`method-step-${m.n}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`relative flex gap-6 lg:gap-10 lg:w-1/2 ${
                  i % 2 === 0 ? "lg:pr-12 lg:text-right lg:ml-0" : "lg:pl-12 lg:ml-auto lg:flex-row-reverse"
                }`}
              >
                <div className={`relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-teal text-white font-display text-lg ${
                  i % 2 === 0 ? "lg:order-2 lg:-mr-[62px]" : "lg:-ml-[62px]"
                }`}>
                  {m.n}
                </div>
                <div>
                  <h3 className="font-display text-xl text-white">{m.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
